import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  COLOR_MAP,
  getTranslatedText,
  BackgroundIcon,
  NoBorderWrapper
} from '@nile/components';
import styled from 'styled-components';
import { isEqual } from 'lodash';
import { injectIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { Autocomplete } from '@shopify/polaris';
import { SVGIcon } from '@superops/super-icons';

const PathComponentContainer = styled.div`
  width: 100%;
  margin-top: -20px;
  font-size: 14px;
  font-weight: 400;
  padding: 12px 48px !important;
  border-bottom: 1px solid #e4e4e4;

  .Polaris-TextField {
    margin-bottom: 0 !important;
  }

  .Superops-TextField__ActionablePrefix,
  .Superops-TextField__ActionableSuffix {
    border: none;
  }
  .Polaris-TextField__Backdrop {
    display: none;
  }
`;

const assetTypeDetailsMap = {
  '3': {
    prefix: null,
    seperator: '/'
  },
  '2': {
    prefix: null,
    seperator: '/'
  },
  '1': {
    prefix: 'asset.details.registryEditor.computer',
    seperator: '\\'
  }
};
const PrefixContainer = styled.span`
  cursor: pointer;
`;

let PathSelector = ({
  currentPath: path,
  content,
  intl,
  openPath,
  assetInfo,
  onIconClick
}) => {
  const currentAssetType =
    assetTypeDetailsMap[assetInfo?.assetClass?.classId || '1'];
  let fullPath = formatPath(path);
  const [deselectedOptions, setDeselectedOptions] = useState([]);
  const [regPath, setRegPath] = useState(fullPath);
  const [currentPath, setCurrentPath] = useState([]);
  const currentPathRef = useRef();
  currentPathRef.current = currentPath;
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');

  function removeConsecutiveSeparators(str, separator) {
    if (!separator) return str;
    const separatorRegex = new RegExp(`${separator}+`, 'g');
    return str.replace(separatorRegex, separator);
  }
  function formatPath(path) {
    let clonedPath = [...path];
    let fullPathString = '';
    if (currentAssetType?.prefix) {
      fullPathString = `${getTranslatedText(intl, currentAssetType.prefix)}${
        currentAssetType.seperator
      }`;
    }
    if (clonedPath[0] === '/') {
      clonedPath[0] = ' ';
    }
    if (clonedPath.length > 0) {
      fullPathString =
        fullPathString +
        clonedPath?.join(currentAssetType.seperator) +
        currentAssetType.seperator;
    }

    fullPathString = removeConsecutiveSeparators(
      fullPathString,
      currentAssetType.seperator
    );

    return fullPathString;
  }

  function resetField() {
    setSelectedOptions([]);
    // setRegPath('');
    setOptions(deselectedOptions);
  }

  const updateSelection = useCallback(
    (selected) => {
      if (selected[0] === 'No Result') {
        resetField();
        return;
      }
      let selectedOption;
      options.map((option) => {
        if (option.value === selected[0]) {
          selectedOption = option;
        }
      });
      if (!selectedOption) return;
      if (selectedOption.id === 'custom') {
        setRegPath(selected[0]);
        let inputValue = selectedOption.value.trim();
        let translatedPrefix = null;
        if (currentAssetType.prefix) {
          translatedPrefix = getTranslatedText(intl, currentAssetType.prefix);
        }

        // Process paths differently based on asset class
        const assetClassId = assetInfo?.assetClass?.classId || '1';

        if (assetClassId === '1') {
          // Windows
          // For Windows, we need to handle the "Computer\" prefix
          if (translatedPrefix) {
            // Handle case with separator: "Computer\C:\Users"
            if (
              inputValue.startsWith(
                (translatedPrefix + currentAssetType.seperator).toLowerCase()
              )
            ) {
              inputValue = inputValue.substring(
                translatedPrefix.length + currentAssetType.seperator.length
              );
            }
            // Handle case without separator: "ComputerC:\Users"
            else if (inputValue.startsWith(translatedPrefix)) {
              inputValue = inputValue.substring(translatedPrefix.length);
            }
          }

          let typedPath = inputValue
            .split(currentAssetType.seperator)
            .filter((segment) => segment.trim() !== '');

          openPath(typedPath, true);
        } else {
          // Linux/Mac
          // Ensure path has proper separator
          if (!inputValue.startsWith('/')) {
            inputValue = '/' + inputValue;
          }

          let typedPath = inputValue.split(currentAssetType.seperator);

          if (typedPath[0] === '') {
            typedPath[0] = '/';
          }

          // Filter out empty segments except first "/"
          typedPath = typedPath.filter(
            (segment, index) =>
              (index === 0 && segment === '/') || segment.trim() !== ''
          );

          openPath(typedPath, true);
        }
        return;
      }
      handlePathSelect(selected[0]);
    },
    [options]
  );

  useEffect(() => {
    setRegPath(fullPath);
  }, [content, path]);

  useEffect(() => {
    let TimedUpdate = setTimeout(() => {
      if (regPath) {
        let typedPath = regPath.split(currentAssetType.seperator);
        let correctedPath;
        if (currentAssetType.prefix) {
          correctedPath = typedPath.slice(1, typedPath.length - 1);
        } else {
          if (typedPath[0] === ' ') {
            typedPath[0] = '/';
          }
          correctedPath = typedPath.slice(0, typedPath.length - 1);
        }
        let keyword = typedPath[typedPath.length - 1];
        setSearchKeyword(keyword);
        setCurrentPath(correctedPath);
      }
    }, 500);

    return () => clearTimeout(TimedUpdate);
  }, [regPath]);

  const handlePathSelect = (selectedPath) => {
    if (currentPathRef?.current && currentPathRef?.current?.length > 0) {
      openPath([...currentPathRef.current, selectedPath]);
    } else {
      openPath([selectedPath]);
    }
  };

  useEffect(() => {
    handlePathChange(currentPath);
  }, [currentPath, content, path]);

  const handlePathChange = (givenPath) => {
    let currentPathData = [];
    Object.keys(content).filter((ke) => {
      let arnew = content[ke]?.parent;
      return (
        content[ke]?.index !== 'root' &&
        isEqual(arnew, givenPath) &&
        currentPathData.push(content[ke])
      );
    });

    let currentOptions = currentPathData.map((info, id) => {
      return {
        label: formatPath(givenPath) + info.data,
        value: info.data,
        id: id
      };
    });

    let resultOptions = currentOptions;

    let filtereredResults = resultOptions.filter((result) =>
      result?.value
        .toLocaleLowerCase()
        .includes(searchKeyword.toLocaleLowerCase())
    );

    if (regPath && filtereredResults.length === 0) {
      filtereredResults = [
        {
          label: regPath,
          value: regPath,
          id: 'custom'
        }
      ];
    }

    setOptions(filtereredResults);

    setDeselectedOptions(currentOptions);
  };

  const activator = (
    <PathComponentContainer>
      <NoBorderWrapper>
        <Autocomplete.TextField
          onChange={setRegPath}
          value={regPath}
          prefix={
            <PrefixContainer onClick={onIconClick}>
              <div className="fx center">
                <SVGIcon name="PathComponent" />
              </div>
            </PrefixContainer>
          }
          placeholder={''}
        />
      </NoBorderWrapper>
    </PathComponentContainer>
  );

  return (
    <div style={{ marginTop: '20px' }}>
      <Autocomplete
        options={options}
        selected={selectedOptions}
        onSelect={updateSelection}
        textField={activator}
      />
    </div>
  );
};

let PathComponent = injectIntl(PathSelector);

export { PathComponent };
